import { Injectable } from '@angular/core';
import { Observable, of, map, catchError } from 'rxjs';
import { ORIGEM_RENDA } from '../../../../shared/constants/dados-complementares/origem-renda.const';
import { PROFISSOES } from '../../../../shared/constants/dados-complementares/profissoes.const';
import { Autocomplete } from '../../../../shared/interfaces/dados-complementares/autocomplete.interface';
import { IDominioResponseQA } from '../../../../shared/interfaces/dados-pessoais/dominio-qa-response.interface';
import { EnderecoCW3 } from '../../../../shared/interfaces/dados-cadastrais/cep-responseCW3.interface';
import { ValorDominioOrigemRenda } from '../../../../shared/interfaces/dados-complementares/origem-renda-reponse.interface';
import { ValorDominioProfissao } from '../../../../shared/interfaces/dados-complementares/profissao-reponse.interface';
import { RequestCaronteService } from 'src/app/abertura-conta/config/service/request-caronte/request-caronte.service';
import { ListaSelect } from '../../../../shared/interfaces/dados-cadastrais/lista-select.interface';
import { LISTA_NACIONALIDADE } from 'src/app/shared/constants/dados-cadastrais/nacionalidade.const';
import { LISTA_OPCAO_SEXO } from 'src/app/shared/constants/dados-cadastrais/opcao-sexo.const';
import { IPais } from '../../../../shared/interfaces/dados-pessoais/paises-cw3-response.interface';
import { PAISES } from '../../../../shared/constants/dados-complementares/paises.const';
import { Estado } from '../../../../shared/interfaces/dados-endereco/lista-estados-response.interface';
import { Cidade } from '../../../../shared/interfaces/dados-endereco/lista-cidades-response.interface';

@Injectable({ providedIn: 'root' })
export class DominioQaService {

  private listaProfissoes: Autocomplete[];
  private listaOrigensRenda: Autocomplete[];
  private listaNacionalidade: ListaSelect[]; /* armazena a lista normalizada para evitar requisicoes e processamento desnecessario */
  public listaSexo: ListaSelect[]; /* armazena a lista normalizada para evitar requisicoes e processamento desnecessario */
  private listaPaises: IPais[]; /* armazena a lista normalizada para evitar requisicoes e processamento desnecessario */
  private listaEstados: Estado[]; /* armazena a lista normalizada para evitar requisicoes e processamento desnecessario */
  constructor(private requestCaronteService: RequestCaronteService) { }


  /**
  * Obtém a lista de profissões.
  * Se a lista já estiver carregada, retorna os dados armazenados localmente.
  * Caso contrário, faz uma chamada ao serviço para buscar os dados.
  * @returns Observable contendo uma lista de objetos Autocomplete com as profissões.
  */
  public getProfissaoCaronte(): Observable<Autocomplete[]> {
    if (this.listaProfissoes) {
      return of(this.listaProfissoes);
    }

    return this.requestCaronteService.getCaronte<IDominioResponseQA>("consultarOcupacao")
      .pipe(
        map(response => {
          if (response) {
            return this.setValueProfissao(response.data);
          }
          throw new Error('Response is null');
        }),
        catchError(() => {
          return of(this.setValueProfissao(PROFISSOES));
        })
      );
  }

  /**
   * Obtém a lista de origens de renda.
   * Se a lista já estiver carregada, retorna os dados armazenados localmente.
   * Caso contrário, faz uma chamada ao serviço para buscar os dados.
   * @returns Observable contendo uma lista de objetos Autocomplete com as origens de renda.
   */
  public getOrigemRenda(): Observable<Autocomplete[]> {
    if (this.listaOrigensRenda) {
      return of(this.listaOrigensRenda);
    }

    return this.requestCaronteService.getCaronte<IDominioResponseQA>("consultarOrigemRenda")
      .pipe(
        map(response => {
          if (response) {
            return this.setValueOriremRenda(response.data);
          }
          throw new Error('Response is null');
        }),
        catchError(() => {
          return of(this.setValueOriremRenda(ORIGEM_RENDA));
        })
      );
  }

  /**
  * Obtém informações de endereço com base no CEP fornecido.
  * @param cep Código postal (CEP) para busca do endereço.
  * @returns Observable contendo um objeto EnderecoCW3 com os dados do endereço.
  */
  public getEnderecoByCep(cep: string): Observable<EnderecoCW3> {
    return this.requestCaronteService.getCaronte<EnderecoCW3>('consultarCep', [{ chave: 'cep', valor: cep }]).pipe(
      map((response) => {
        return response.data;
      }));
  }

  /* recupera os dados de nacionalidades */
  public getNacionalidadeDominioQA(): Observable<ListaSelect[]> {

    if (this.listaNacionalidade)
      return of(this.listaNacionalidade);

    return this.requestCaronteService.getCaronte<IDominioResponseQA>("consultarNacionalidade")
      .pipe(
        map((response) => {

          if (response)
            return this.setListaNacionalidade(response.data);

          throw new Error('Response is null');
        }),
        catchError(() => {
          // Em caso de erro, retorna a lista de fallback
          return of(this.setListaNacionalidade(LISTA_NACIONALIDADE));
        })
      );
  }

  /* recupera os dados de sexo e normaliza eles no padrao IDS */
  public GetSexoDominioQA(): Observable<ListaSelect[]> {

    if (this.listaSexo)
      return of(this.listaSexo);

    return this.requestCaronteService.getCaronte<IDominioResponseQA>("consultarSexo")
      .pipe(
        map((response) => {

          if (response) {

            const lista = (response.data.valores_dominio as unknown as any[]).map(sexo => ({
              optLabel: this.capitalizeFirstLetter(sexo.nome_completo_valor_dominio),
              optValue: sexo.codigo_referencia_valor_dominio
            }));

            this.listaSexo = lista;

            return lista;
          }
          throw new Error('Response is null');
        }),
        catchError(() => {
          // Em caso de erro, retorna a lista de fallback
          this.listaSexo = LISTA_OPCAO_SEXO;
          return of(LISTA_OPCAO_SEXO);
        })
      );
  }

  public GetPaisesCW3(): Observable<IPais[]> {
    if (this.listaPaises)
      return of(this.listaPaises);

    return this.requestCaronteService.getCaronte<IPais[]>("consultarPaises")
      .pipe(
        map(response => {
          if (response) {
            this.listaPaises = response.data;
            return response.data;
          }
          throw new Error('Response is null');
        }),
        catchError(() => {
          const fallback: IPais[] = PAISES.data;
          return of(fallback);
        })
      );
  }

  public GetUnidadesFederativas(): Observable<Estado[]> {
    if (this.listaEstados)
      return of(this.listaEstados);

    return this.requestCaronteService.getCaronte<Estado[]>("consultarUnidadesFederativas")
      .pipe(
        map(response => {
          if (response) {
            this.listaEstados = response.data;
            return response.data;
          }
          throw new Error('Response is null');
        })
      );

  }

  public GetCidadesPorUF(siglaUf: string): Observable<Cidade[]> {
    return this.requestCaronteService.getCaronte<Cidade[]>("consultarCidadesPorUF", [{ chave: 'sigla_uf', valor: siglaUf }])
      .pipe(
        map(response => {
          if (response) {
            return response.data;
          }
          throw new Error('Response is null');
        })
      );
  }

  public removeSiglaETraço(name: string): string {
    return name.replace(/^[A-Z]{2} - /, '');
  }

  // Função para capitalizar a primeira letra
  private capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  /* normaliza a lista de valores do dominio QA para o padrao IDS */
  private setListaNacionalidade(response: IDominioResponseQA): ListaSelect[] {

    const lista = (response.valores_dominio as unknown as any[]).map(nacionalidade => ({
      optLabel: this.capitalizeFirstLetter(nacionalidade.nome_completo_valor_dominio),
      optValue: nacionalidade.codigo_valor_dominio
    }));

    // Coloca "Brasileiro" como o primeiro item da lista
    const brasileiroIndex = lista.findIndex(item => item.optValue === 648);
    if (brasileiroIndex > -1) {
      const [brasileiro] = lista.splice(brasileiroIndex, 1);
      lista.unshift(brasileiro);
    }

    this.listaNacionalidade = lista;

    return lista;
  }

  /**
 * Normaliza e processa a lista de profissões recebida do serviço externo.
 * @param listaQA Objeto contendo os dados de profissões retornados pelo serviço.
 * @returns Uma lista de objetos Autocomplete com as profissões normalizadas.
 */
  private setValueProfissao(listaQA: IDominioResponseQA): Autocomplete[] {
    let listaProfissoesNormalizada: Autocomplete[] = [];
    let valorDominioPrioritario: Autocomplete | null = null;
    listaQA.valores_dominio.forEach((element: ValorDominioProfissao) => {
      if (element.nome_completo_valor_dominio.includes('\u00A0')) {
        let result = element.nome_completo_valor_dominio.replace(/\u00A0/g, '-');
        result = result.replace(/-/g, ' ');
        result = this.capitalizeWords(result);

        if (element.codigo_valor_dominio === 2668) {
          valorDominioPrioritario = { name: result, value: element.codigo_valor_dominio };
          return;
        }

        listaProfissoesNormalizada.push({ name: result, value: element.codigo_valor_dominio });
      } else {
        if (element.codigo_valor_dominio === 2668) {
          valorDominioPrioritario = { name: this.capitalizeWords(element.nome_completo_valor_dominio), value: element.codigo_valor_dominio };
          return;
        }

        listaProfissoesNormalizada.push({ name: this.capitalizeWords(element.nome_completo_valor_dominio), value: element.codigo_valor_dominio });
      }
    });

    listaProfissoesNormalizada.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

    if (valorDominioPrioritario) {
      listaProfissoesNormalizada.unshift(valorDominioPrioritario);
    }

    this.listaProfissoes = listaProfissoesNormalizada;
    return listaProfissoesNormalizada;
  }

  /**
* Normaliza uma string, convertendo-a para o formato "Primeira Letra Maiúscula".
* @param input String a ser normalizada.
* @returns String com a primeira letra de cada palavra em maiúscula e o restante em minúscula.
*/
  private capitalizeWords(input: string): string {
    try {
      return input.split(' ').map(word => {
        if (word.length > 2) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        } else {
          return word.toLowerCase();
        }
      }).join(' ');
    } catch (error) {
      return input;
    }
  }

  /**
   * Normaliza e processa a lista de origens de renda recebida do serviço externo.
   * @param listaQA Objeto contendo os dados de origens de renda retornados pelo serviço.
   * @returns Uma lista de objetos Autocomplete com as origens de renda normalizadas.
   */
  private setValueOriremRenda(listaQA: IDominioResponseQA): Autocomplete[] {
    let listaOrigensRendaNormalizada: Autocomplete[] = [];
    let proprietarioRural: Autocomplete | null = null;

    listaQA.valores_dominio.forEach((element: ValorDominioOrigemRenda) => {
      const partes = element.nome_completo_valor_dominio.split('\u00A0');
      const stringComEspacosNormais = partes.join(' ');

      let result = stringComEspacosNormais.replace(/\s/g, '-').replace(/\u00A0/g, '-').replace(/&nbsp;/g, '-');
      result = result.replace(/-/g, ' ');

      if (element.codigo_valor_dominio === 11833)
        proprietarioRural = { name: this.capitalizeWords(result), value: element.codigo_valor_dominio };
      else
        listaOrigensRendaNormalizada.push({ name: this.capitalizeWords(result), value: element.codigo_valor_dominio });
    });

    listaOrigensRendaNormalizada.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

    if (proprietarioRural) {
      listaOrigensRendaNormalizada.unshift(proprietarioRural);
    }

    this.listaOrigensRenda = listaOrigensRendaNormalizada;
    return listaOrigensRendaNormalizada;
  }

}
